// WelderContinuity.jsx: Welder continuity register (2026-09-29).
// Each welder qualification from GET /api/welding/welder-qualifications (tenant scoped):
// WQTR, process, last weld on record, and the date six months after it. The server
// returns last_weld_on_record (qualification record or completed SFM weld task),
// the same date the weld-task gate uses (S-17b).
// AWS D1.1 keeps a welder qualification in effect unless the welder has not used
// the process for six months. This page reports the dates on record only.

import React, { useEffect, useMemo, useState } from "react";
import ApiCall from "../../utils/apicall";
import { StatusPill } from "../../components/WeldAssure";
import RegisterShell, { fmtDate, daysSince, parseDay } from "./RegisterShell";

export function sixMonthsAfter(v) {
  const d = parseDay(v);
  if (!d) return null;
  const e = new Date(d);
  e.setMonth(e.getMonth() + 6);
  return e;
}

export const lastWeldOf = (w) => w.last_weld_on_record || w.last_weld_date || null;

export function continuityState(lastWeld, now = new Date()) {
  const end = sixMonthsAfter(lastWeld);
  if (!end) return { label: "No weld on record", kind: "neutral", daysLeft: null, end: null };
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const daysLeft = Math.round((end - today) / 86400000);
  if (daysLeft < 0) return { label: "Six months passed", kind: "danger", daysLeft, end };
  if (daysLeft <= 30) return { label: `${daysLeft} days left`, kind: "warning", daysLeft, end };
  return { label: "Within six months", kind: "success", daysLeft, end };
}

export default function WelderContinuity() {
  const [quals, setQuals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await ApiCall.get("/welding/welder-qualifications");
        setQuals(Array.isArray(res.data) ? res.data : (res.data?.data || []));
      } catch (err) { setError(err?.response?.data?.message || err?.response?.data?.error || "Could not load welder qualifications."); }
      finally { setLoading(false); }
    })();
  }, []);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return quals
      .map((w) => ({ ...w, c: continuityState(lastWeldOf(w)) }))
      .filter((w) => !q || [w.welder_name, w.process, w.wqtr_number, w.welding_code].some((v) => String(v || "").toLowerCase().includes(q)))
      .sort((a, b) => (a.c.daysLeft ?? -99999) - (b.c.daysLeft ?? -99999));
  }, [quals, search]);

  const states = quals.map((w) => continuityState(lastWeldOf(w)));
  const kpis = [
    { label: "QUALIFICATIONS", value: quals.length },
    { label: "WITHIN SIX MONTHS", value: states.filter((s) => s.kind === "success").length },
    { label: "30 DAYS OR LESS", value: states.filter((s) => s.kind === "warning").length },
    { label: "PASSED OR NO RECORD", value: states.filter((s) => s.kind === "danger" || s.kind === "neutral").length },
  ];

  return (
    <RegisterShell
      title="Welder Continuity"
      subtitle="Each welder qualification, the last weld on record in that process, and when six months from it ends."
      guide={{ why: "See which welder qualifications are close to, or past, six months without a recorded weld in the process.", who: "Welding supervisors and QC.", what: "Welder, code, process, WQTR, last recorded weld, and the six-month date.", how: "Sorted by fewest days left. Dates come from the qualification record.", when: "Weekly, and before assigning welds.", standard: "AWS D1.1 welder qualification period of effectiveness" }}
      kpis={kpis}
      columns={["WELDER", "CODE", "PROCESS", "WQTR", "LAST WELD ON RECORD", "SIX MONTHS ENDS", "CONTINUITY", "STATUS ON RECORD"]}
      rows={rows}
      loading={loading}
      error={error}
      search={search}
      onSearch={setSearch}
      searchPlaceholder="Welder, process or WQTR"
      emptyText="No welder qualifications on file."
      footnote="Last weld on record is the later of the qualification record's last weld date and the latest completed SFM weld task in that process; the weld-task gate uses the same date. Status on record is what was last entered or synced; it is shown as is."
      renderRow={(w) => (
        <tr key={w.id}>
          <td>{w.welder_name}</td>
          <td>{w.welding_code}</td>
          <td>{w.process}</td>
          <td style={{ fontFamily: "monospace" }}>{w.wqtr_number || ""}</td>
          <td>{fmtDate(lastWeldOf(w))}{lastWeldOf(w) ? ` (${daysSince(lastWeldOf(w))} days ago)` : ""}</td>
          <td>{w.c.end ? fmtDate(w.c.end) : ""}</td>
          <td><StatusPill label={w.c.label} kind={w.c.kind} /></td>
          <td>{w.continuity_status || w.status || ""}{w.continuity_last_verified ? `, synced ${fmtDate(w.continuity_last_verified)}` : ""}</td>
        </tr>
      )}
    />
  );
}
